import { MouseEvent, useState, useEffect } from 'react';
import { ThumbsUp } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { useLikePostMutation, useUnlikePostMutation } from './like.api.slice';
import { setShowLoginDialog } from '../global/global.slice';
import { UserState } from '../auth/auth.d';

function LikeButton({
  liked,
  likes,
  postId,
  size = 'sm',
}: {
  liked: boolean | undefined;
  likes: number;
  postId: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const dispatch = useDispatch();
  const { user } = useSelector((state: { auth: UserState }) => state.auth);
  const [like] = useLikePostMutation();
  const [unlike] = useUnlikePostMutation();

  // Optimistic local state: reflect the click immediately instead of waiting
  // for the server round-trip / cache refetch (which can lag or not refetch the
  // feed at all). Re-synced from props whenever the underlying query refetches.
  const [isLiked, setIsLiked] = useState(liked ?? false);
  const [likeCount, setLikeCount] = useState(likes);
  useEffect(() => {
    setIsLiked(liked ?? false);
    setLikeCount(likes);
  }, [liked, likes]);

  // Optimistic toggle: user guard + like/unlike direction + count delta +
  // error-revert — inherently branchy, not reducible.
  // fallow-ignore-next-line complexity
  const handleToggle = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!user) {
      dispatch(setShowLoginDialog(true));
      return;
    }

    const wasLiked = isLiked;
    setIsLiked(!wasLiked);
    setLikeCount(wasLiked ? likeCount - 1 : likeCount + 1);

    try {
      if (wasLiked) {
        await unlike(postId).unwrap();
      } else {
        await like(postId).unwrap();
      }
    } catch {
      // Revert on failure
      setIsLiked(wasLiked);
      setLikeCount(likeCount);
    }
  };

  if (isLiked) {
    return (
      <Button
        variant="secondary"
        size={size}
        className="flex items-center gap-4 justify-center bg-white border-gray-400 border-[1.5px] text-black text-[13px] hover:bg-white"
        onClick={handleToggle}
      >
        <ThumbsUp fill="#292929" stroke="#292929" size={16} />
        <p>{likeCount}</p>
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      size={size}
      onClick={handleToggle}
      className="flex items-center gap-4 justify-center text-[13px]"
    >
      <ThumbsUp stroke="#292929" size={16} />
      <p>{likeCount}</p>
    </Button>
  );
}

export default LikeButton;
